import { useEffect, useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useSelector, dispatch } from 'store'
import { openDrawer } from 'store/reducers/menu'
import {
	Home3,
	Element3,
	Box1,
	WalletMoney,
	StatusUp,
	People,
	Sms,
	Message,
	HambergerMenu,
	Setting2,
	Logout,
} from 'iconsax-react'
import useAuth from 'hooks/useAuth'

interface MenuItem {
	id: string
	title: string
	url: string
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	icon: any
	external?: boolean
	target?: boolean
	vip?: boolean
}

interface MenuGroup {
	id: string
	title: string
	items: MenuItem[]
}

const menuGroups: MenuGroup[] = [
	{
		id: 'main',
		title: 'Studio',
		items: [
			{ id: 'dashboard', title: 'Dashboard', url: '/dashboard/home', icon: Home3 },
			{ id: 'series', title: 'Series', url: '/series', icon: Element3 },
			{ id: 'collectibles', title: 'Collectibles', url: '/collectibles', icon: Box1 },
			{ id: 'wallet', title: 'Wallet', url: '/wallet', icon: WalletMoney },
			{ id: 'campaigns', title: 'Campaigns', url: '/coupons/create', icon: StatusUp, vip: true },
			{ id: 'subscribers', title: 'Subscribers', url: '/subscribers', icon: People },
		],
	},
	{
		id: 'support',
		title: 'Support',
		items: [
			{ id: 'email', title: 'Email', url: 'mailto:support@toontube.com', icon: Sms, external: true },
			{ id: 'chat', title: 'Chat', url: 'https://wa.me/972546632161', icon: Message, external: true, target: true },
		],
	},
]

const MainLayout = () => {
	const location = useLocation()
	const { logout } = useAuth()
	const { drawerOpen } = useSelector((state) => state.menu)
	const [isMobile, setIsMobile] = useState(false)

	useEffect(() => {
		const checkMobile = () => setIsMobile(window.innerWidth < 1024) // lg breakpoint
		checkMobile()
		window.addEventListener('resize', checkMobile)
		return () => window.removeEventListener('resize', checkMobile)
	}, [])

	const handleDrawerToggle = () => {
		dispatch(openDrawer({ drawerOpen: !drawerOpen }))
	}

	const isActive = (url: string) => {
		if (url === '/dashboard/home') return location.pathname.startsWith('/dashboard')
		return location.pathname.startsWith(url)
	}

	const handleLogout = async () => {
		try {
			await logout()
		} catch (err) {
			console.error(err)
		}
	}

	const renderMenuItem = (item: MenuItem) => {
		const Icon = item.icon
		const active = !item.external && isActive(item.url)

		if (item.external) {
			return (
				<li key={item.id}>
					<a
						href={item.url}
						target={item.target ? '_blank' : undefined}
						rel={item.target ? 'noopener noreferrer' : undefined}
						className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-white transition-all text-sm"
					>
						<Icon size={18} variant="Bulk" />
						<span>{item.title}</span>
					</a>
				</li>
			)
		}

		return (
			<li key={item.id}>
				<Link
					to={item.url}
					className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
						active
							? 'bg-indigo-500/10 text-indigo-400 font-medium'
							: 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
					}`}
					onClick={() => isMobile && handleDrawerToggle()}
				>
					<Icon size={18} variant={active ? 'Bold' : 'Bulk'} className={active ? 'text-indigo-400' : ''} />
					<span>{item.title}</span>
					{item.vip && (
						<span className="ml-auto px-1.5 py-0.5 text-[10px] font-medium bg-amber-500/20 text-amber-400 rounded">
							PRO
						</span>
					)}
				</Link>
			</li>
		)
	}

	const sidebarContent = (
		<div className="flex flex-col h-full bg-zinc-900 border-r border-zinc-800">
			{/* Logo / Brand */}
			<div className="p-5 border-b border-zinc-800">
				<Link to="/dashboard/home" className="flex items-center gap-3">
					<div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
						<span className="text-lg font-bold text-white">T</span>
					</div>
					<div>
						<h1 className="text-base font-bold text-white">Toontube</h1>
						<p className="text-xs text-zinc-500">Creator Studio</p>
					</div>
				</Link>
			</div>

			{/* Navigation */}
			<nav className="flex-1 overflow-y-auto px-3 py-4">
				{menuGroups.map((group) => (
					<div key={group.id} className="mb-6">
						<h2 className="px-3 mb-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
							{group.title}
						</h2>
						<ul className="space-y-0.5">
							{group.items.map(renderMenuItem)}
						</ul>
					</div>
				))}
			</nav>

			{/* Bottom Section */}
			<div className="p-3 border-t border-zinc-800">
				<ul className="space-y-0.5">
					<li>
						<Link
							to="/settings"
							className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-white transition-all text-sm"
						>
							<Setting2 size={18} variant="Bulk" />
							<span>Settings</span>
						</Link>
					</li>
					<li>
						<button
							onClick={handleLogout}
							className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:bg-red-500/10 hover:text-red-400 transition-all w-full text-sm"
						>
							<Logout size={18} variant="Bulk" />
							<span>Logout</span>
						</button>
					</li>
				</ul>
			</div>
		</div>
	)

	return (
		<div className="min-h-screen bg-zinc-950 flex">
			{/* Sidebar - Desktop */}
			<aside className="hidden lg:flex lg:w-60 lg:flex-col lg:fixed lg:inset-y-0 z-50">
				{sidebarContent}
			</aside>

			{/* Mobile sidebar overlay */}
			{isMobile && drawerOpen && (
				<div className="fixed inset-0 z-40 lg:hidden">
					<div
						className="fixed inset-0 bg-black/60 backdrop-blur-sm"
						onClick={handleDrawerToggle}
					/>
					<aside className="fixed inset-y-0 left-0 w-60 z-50">
						{sidebarContent}
					</aside>
				</div>
			)}

			{/* Main content */}
			<div className="flex-1 lg:ml-60 min-w-0 overflow-x-hidden">
				{/* Header */}
				<header className="sticky top-0 z-30 flex items-center gap-4 h-14 px-4 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800 lg:px-6">
					<button
						onClick={handleDrawerToggle}
						className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white lg:hidden transition-colors"
					>
						<HambergerMenu size={20} />
					</button>
					<div className="flex-1" />
				</header>

				{/* Page content */}
				<main className="p-4 lg:p-6 min-w-0 overflow-x-auto">
					<Outlet />
				</main>
			</div>
		</div>
	)
}

export default MainLayout
